import React, { useMemo, useState } from 'react';
import type { AppState, RolPermiso } from '../types';
import { PageShell, glassLight, primaryButton, ghostButton, Badge, EmptyState, useIsMobile, usePagination, Pagination, T } from '../ui';
// Colores fijos para los primeros roles; el resto usa un color genérico
const ROL_COLORS = [{
  color: '#8b5cf6',
  soft: 'rgba(139,92,246,0.14)'
}, {
  color: T.info,
  soft: T.infoSoft
}, {
  color: T.accent,
  soft: T.accentSoft
}, {
  color: '#ec4899',
  soft: 'rgba(236,72,153,0.14)'
}, {
  color: '#10b981',
  soft: 'rgba(16,185,129,0.14)'
}];
interface Props {
  state: AppState;
  onNew: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  canArchive: boolean;
}
export function Usuarios({
  state,
  onNew,
  onEdit,
  onDelete,
  canArchive
}: Props) {
  const [tab, setTab] = useState<'activos' | 'archivados'>('activos');
  const isMobile = useIsMobile();
  // Construir etiquetas y colores dinámicamente desde los roles reales
  const rolLabel: Record<string, string> = {};
  const rolColor: Record<string, {
    color: string;
    soft: string;
  }> = {};
  state.roles.forEach((r, i) => {
    const key = r.id.replace(/^rol-/, '');
    rolLabel[key] = r.nombre;
    rolColor[key] = ROL_COLORS[i % ROL_COLORS.length];
  });
  const filteredUsuarios = useMemo(() => {
    return state.usuarios.filter(u => tab === 'activos' ? !u.archivado : u.archivado);
  }, [state.usuarios, tab]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filteredUsuarios, 8);
  const sucNamesFor = (ids?: string[]) => {
    const safeIds = Array.isArray(ids) ? ids : [];
    return safeIds.length === 0 ? <span style={{
      color: '#c4b5fd',
      fontStyle: 'italic'
    }} data-id="element-1179" data-mp="39hevocmyflpl">
      
        Todas las sucursales
      </span> : safeIds.map(sid => state.sucs.find(x => x.id === sid)?.nombre ?? sid).join(', ');
  };
  return <PageShell title="Usuarios y acceso" sub="Gestiona quién puede entrar y qué sucursales puede ver" action={<button onClick={onNew} style={primaryButton} data-id="element-1181" data-mp="fotxdikcnfu0">
          + Nuevo usuario
        </button>} data-id="element-1180" data-mp="5uwoc5kwr33m">
      
      <div style={{
      display: 'flex',
      gap: 8,
      marginBottom: 20
    }} data-id="element-1182" data-mp="ar0o2gnr25pi">
        
        <button onClick={() => {
        setTab('activos');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'activos' ? T.accentSoft : 'transparent',
        color: tab === 'activos' ? T.accent : T.textMuted
      }} data-id="element-1183" data-mp="5pbe74c9jy30">
          
          Activos
        </button>
        {canArchive && <button onClick={() => {
        setTab('archivados');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'archivados' ? T.accentSoft : 'transparent',
        color: tab === 'archivados' ? T.accent : T.textMuted
      }} data-id="element-1184" data-mp="3o3athrx3tzkp">
          
            Archivados
          </button>}
      </div>

      {filteredUsuarios.length === 0 ? <EmptyState title="Sin usuarios registrados" hint="Agrega usuarios y define a qué sucursales tienen acceso." icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" data-id="element-1186" data-mp="1kqbk6isio3nw">
          
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" data-id="element-1187" data-mp="1tq8eazxoduqc" />
              <circle cx="9" cy="7" r="4" data-id="element-1188" data-mp="1yojojbrvu793" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" data-id="element-1189" data-mp="1yni1e8xldub0" />
            </svg>} data-id="element-1185" data-mp="356t59khgsht7" /> : isMobile ? <>
          <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }} data-id="element-1190" data-mp="111kmelif873t">
          
            {pageItems.map(u => {
          const rc = rolColor[u.rol] ?? {
            color: T.textMuted,
            soft: 'rgba(255,255,255,0.06)'
          };
          return <div key={u.id} style={{
            ...glassLight,
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 12
          }} data-id="element-1191" data-mp="1fq7cni9l6j9t">
                
                  <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 10
            }} data-id="element-1192" data-mp="n7jvgdxpnoe0">
                  
                    <div style={{
                minWidth: 0
              }} data-id="element-1193" data-mp="2ky08yzo2gmot">
                    
                      <div style={{
                  fontWeight: 700,
                  color: T.text,
                  fontSize: 15
                }} data-id="element-1194" data-mp="2ypetjaxmgvxz">
                      
                        {u.nombre}
                      </div>
                      <div style={{
                  fontSize: 13,
                  color: T.textSecondary,
                  marginTop: 2,
                  wordBreak: 'break-all'
                }} data-id="element-1195" data-mp="33n6tms8f7od9">
                      
                        {u.correo}
                      </div>
                    </div>
                    <div style={{
                flexShrink: 0
              }} data-id="element-1196" data-mp="1n859am6o5zvm">
                    
                      <Badge label={rolLabel[u.rol]} color={rc.color} soft={rc.soft} data-id="element-1197" data-mp="1iadhds60ydrf" />
                    
                    </div>
                  </div>
                  <div data-id="element-1198" data-mp="3tz0f4r8epdjf">
                    <div style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: T.textMuted,
                marginBottom: 4
              }} data-id="element-1199" data-mp="2qcw4hxn22f1">
                    
                      Sucursales asignadas
                    </div>
                    <div style={{
                fontSize: 13,
                color: T.textSecondary,
                lineHeight: 1.45
              }} data-id="element-1200" data-mp="3b54njoe4cqvd">
                    
                      {sucNamesFor(u.sucursalesIds)}
                    </div>
                  </div>
                  <div style={{
              display: 'flex',
              gap: 8,
              paddingTop: 4,
              borderTop: `1px solid ${T.border}`
            }} data-id="element-1201" data-mp="368ebdc5fqff8">
                  
                    <button className="getr-btn-ghost" onClick={() => onEdit(u.id)} style={{
                ...ghostButton,
                flex: 1,
                marginTop: 8
              }} data-id="element-1202" data-mp="14eye3weue2ba">
                    
                      Editar
                    </button>
                    {u.rol !== 'admin' && <button onClick={() => onDelete(u.id)} style={{
                ...ghostButton,
                flex: 1,
                marginTop: 8,
                color: u.archivado ? T.info : T.warn,
                borderColor: u.archivado ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)',
                background: u.archivado ? 'rgba(96,165,250,0.1)' : 'rgba(251,191,36,0.1)'
              }} data-id="element-1203" data-mp="24d9xhflkw91z">
                    
                        {u.archivado ? 'Desarchivar' : 'Archivar'}
                      </button>}
                  </div>
                </div>;
        })}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="usuarios" data-id="element-1204" data-mp="3fsgmn5wiulsr" />
        
        </> : <div style={{
      ...glassLight,
      overflow: 'hidden'
    }} data-id="element-1205" data-mp="3ic3am2f1pxxx">
        
          <div style={{
        overflowX: 'auto'
      }} data-id="element-1206" data-mp="3nawnptqf7mxk">
          
            <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13
        }} data-id="element-1207" data-mp="3nb6e3ifdnw3s">
            
              <thead data-id="element-1208" data-mp="5yq61116d9z">
                <tr style={{
              background: T.surfaceSubtle
            }} data-id="element-1209" data-mp="6gqp9uq7dnp">
                
                  {['Usuario', 'Correo', 'Rol', 'Sucursales asignadas', 'Acciones'].map(h => <th key={h} style={{
                padding: '13px 16px',
                fontWeight: 700,
                fontSize: 10,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: T.textMuted,
                textAlign: 'left',
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-1210" data-mp="a3uxehquko2t">
                  
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-1211" data-mp="1e5udmxiwd1zz">
                {pageItems.map((u, i) => {
              const uSucIds = Array.isArray(u.sucursalesIds) ? u.sucursalesIds : [];
              const sucNames = uSucIds.length === 0 ? <span style={{
                color: '#c4b5fd',
                fontStyle: 'italic'
              }} data-id="element-1212" data-mp="1993yqditwcg5">
                  
                        Todas las sucursales
                      </span> : uSucIds.map(sid => {
                const s = state.sucs.find(x => x.id === sid);
                return s ? s.nombre : sid;
              }).join(', ');
              const rc = rolColor[u.rol] ?? {
                color: T.textMuted,
                soft: 'rgba(255,255,255,0.06)'
              };
              return <tr key={u.id} className="getr-row" style={{
                background: i % 2 === 1 ? T.surface : 'transparent',
                borderBottom: i < pageItems.length - 1 ? `1px solid ${T.border}` : 'none',
                transition: 'background .15s'
              }} data-id="element-1213" data-mp="10epleghuzbzo" data-mpi={i}>
                    
                      <td style={{
                  padding: '13px 16px',
                  fontWeight: 600,
                  color: T.text
                }} data-id="element-1214" data-mp="2jclvs1l2e2t1">
                      
                        {u.nombre}
                      </td>
                      <td style={{
                  padding: '13px 16px',
                  color: T.textSecondary
                }} data-id="element-1215" data-mp="29gr7apu5tdic">
                      
                        {u.correo}
                      </td>
                      <td style={{
                  padding: '13px 16px'
                }} data-id="element-1216" data-mp="321shlxh5naj2">
                      
                        <Badge label={rolLabel[u.rol]} color={rc.color} soft={rc.soft} data-id="element-1217" data-mp="2t371sa32i9lz" />
                      
                      </td>
                      <td style={{
                  padding: '13px 16px',
                  color: T.textSecondary,
                  maxWidth: 260
                }} data-id="element-1218" data-mp="2x37g7uq62kt1">
                      
                        {sucNames}
                      </td>
                      <td style={{
                  padding: '13px 16px'
                }} data-id="element-1219" data-mp="1b7zlmkirsqgg">
                      
                        <div style={{
                    display: 'flex',
                    gap: 6
                  }} data-id="element-1220" data-mp="1g7lz8v2ig8ps">
                        
                          <button className="getr-btn-ghost" onClick={() => onEdit(u.id)} style={ghostButton} data-id="element-1221" data-mp="12ffndl6e4nw5">
                          
                            Editar
                          </button>
                          {u.rol !== 'admin' && <button onClick={() => onDelete(u.id)} style={{
                      ...ghostButton,
                      color: u.archivado ? T.info : T.warn,
                      borderColor: u.archivado ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)',
                      background: u.archivado ? 'rgba(96,165,250,0.1)' : 'rgba(251,191,36,0.1)'
                    }} data-id="element-1222" data-mp="xgl4ykcc51ck">
                          
                              {u.archivado ? 'Desarchivar' : 'Archivar'}
                            </button>}
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>
          <div style={{
        padding: '4px 16px 14px'
      }} data-id="element-1223" data-mp="25ek514akcmx">
          
            <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="usuarios" data-id="element-1224" data-mp="g0gxuafdr6aa" />
          
          </div>
        </div>}

    </PageShell>;
}